// filterModel.ts — reine (DOM-freie) Logik für die Redesign-Spaltenfilter.
// Ausgelagert aus RdColumnFilter.tsx, damit die Werte-Semantik der neuen
// Filtertypen (dateRange / people) in der node-Testumgebung geprüft werden kann.
// Alles hier ist generisch/opt-in — bestehende Filter (text/dropdown/boolean/boFo)
// bleiben unberührt.

/** Datumsbereich-Filterwert: beide Grenzen optional (nur-von / nur-bis / Bereich).
 *  Werte sind ISO-Datums-Strings `YYYY-MM-DD` (native `<input type="date">`). */
export type DateRangeValue = { von?: string; bis?: string };

/** Normalisiert einen Datumsbereich: leere/whitespace-Strings → `undefined`. */
export function normalizeDateRange(v: DateRangeValue | undefined): DateRangeValue {
  const von = v?.von?.trim() || undefined;
  const bis = v?.bis?.trim() || undefined;
  return { von, bis };
}

/** Aktiv, sobald mindestens eine Grenze gesetzt ist. */
export function isDateRangeActive(v: DateRangeValue | undefined): boolean {
  const n = normalizeDateRange(v);
  return !!(n.von || n.bis);
}

/** Schnellauswahl-Zeiträume des Datumsbereich-Filters. Vorausschauend
 *  (Frist/TWV) bzw. rückblickend (Mails, Anrufe — dort gibt es kein „morgen"). */
export type DatePresetKey =
  | "heute"
  | "morgen"
  | "dieseWoche"
  | "naechsteWoche"
  | "letzte7"
  | "letzte30"
  | "diesesJahr";

export type DatePresetOption = { key: DatePresetKey; label: string };

/** Reihenfolge = Reihenfolge im UI (2x2-Raster). */
export const DATE_PRESETS: ReadonlyArray<DatePresetOption> = [
  { key: "heute", label: "Heute" },
  { key: "morgen", label: "Morgen" },
  { key: "dieseWoche", label: "Diese Woche" },
  { key: "naechsteWoche", label: "Nächste Woche" },
];

/** Rückblickende Schnellauswahl (Spalten mit vergangenen Zeitpunkten). */
export const DATE_PRESETS_RUECKBLICK: ReadonlyArray<DatePresetOption> = [
  { key: "heute", label: "Heute" },
  { key: "letzte7", label: "Letzte 7 Tage" },
  { key: "letzte30", label: "Letzte 30 Tage" },
  { key: "diesesJahr", label: "Dieses Jahr" },
];

// Datumsarithmetik bewusst LOKAL und ohne `toISOString()`: das rechnet nach UTC
// um und liefert je nach Uhrzeit den Vortag. Vorbild ist utils/relativeDay.ts,
// von dort wird aber nichts importiert — die Helfer dort parsen Strings, hier
// wird nur mit `Date` gerechnet und formatiert.
function toIsoDay(d: Date): string {
  const monat = String(d.getMonth() + 1).padStart(2, "0");
  const tag = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${monat}-${tag}`;
}

function plusTage(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Montag der Woche, in der `d` liegt — deutsche Woche: `getDay()` 0 = Sonntag
 *  gehört ans Wochenende, nicht an den Anfang. */
function wochenstart(d: Date): Date {
  return plusTage(d, -((d.getDay() + 6) % 7));
}

/** Konkreter Von/Bis-Bereich eines Schnellauswahl-Zeitraums (lokale Tage). */
export function datePresetRange(key: DatePresetKey, now: Date = new Date()): DateRangeValue {
  const heute = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const spanne = (start: Date, tage: number): DateRangeValue => ({
    von: toIsoDay(start),
    bis: toIsoDay(plusTage(start, tage)),
  });
  switch (key) {
    case "heute":
      return spanne(heute, 0);
    case "morgen":
      return spanne(plusTage(heute, 1), 0);
    case "dieseWoche":
      return spanne(wochenstart(heute), 6);
    case "naechsteWoche":
      return spanne(plusTage(wochenstart(heute), 7), 6);
    case "letzte7":
      return spanne(plusTage(heute, -6), 6);
    case "letzte30":
      return spanne(plusTage(heute, -29), 29);
    case "diesesJahr":
      return { von: toIsoDay(new Date(heute.getFullYear(), 0, 1)), bis: toIsoDay(heute) };
  }
}

/** Zu welchem Schnellauswahl-Zeitraum passt der Wert? (aktive Markierung im UI)
 *  Ein frei gewählter Bereich ergibt `null`. */
export function matchingDatePreset(
  v: DateRangeValue | undefined,
  now: Date = new Date(),
  presets: ReadonlyArray<DatePresetOption> = DATE_PRESETS,
): DatePresetKey | null {
  const n = normalizeDateRange(v);
  if (!n.von || !n.bis) return null;
  for (const p of presets) {
    const r = datePresetRange(p.key, now);
    if (r.von === n.von && r.bis === n.bis) return p.key;
  }
  return null;
}

/**
 * Rollen im Personen-Filter (T-245, PO 12.10.2026: „bearbeiter, zu info, hv
 * filtern"). Eine Spalte kann zusätzlich zur Person die ROLLE wählen lassen
 * („MaF als Hauptverantwortliche"). Die gewählten Rollen stehen mit diesem
 * Präfix im selben Werte-Array wie die User-Ids — so laufen sie ohne eigenen
 * State durch Tabellen-Präferenzen, Navigations-Snapshot und „Als Standard
 * speichern". User-Ids beginnen nie mit dem Präfix.
 */
export const PERSONEN_ROLLE_PRAEFIX = "rolle:";

export type PeopleRoleOption = { id: string; label: string };

/** Werte-Array in gewählte Personen und gewählte Rollen (ohne Präfix) teilen. */
export function teilePersonenAuswahl(values: readonly string[] | undefined): { ids: string[]; rollen: string[] } {
  const ids: string[] = [];
  const rollen: string[] = [];
  for (const v of values ?? []) {
    if (v.startsWith(PERSONEN_ROLLE_PRAEFIX)) rollen.push(v.slice(PERSONEN_ROLLE_PRAEFIX.length));
    else ids.push(v);
  }
  return { ids, rollen };
}

/** Eine Rolle im Werte-Array an- oder abschalten (unveränderlich). */
export function toggleRolle(values: readonly string[] | undefined, rolle: string): string[] {
  return togglePerson(values, PERSONEN_ROLLE_PRAEFIX + rolle);
}

/**
 * Personen-(Mehrfach-)Filter ist aktiv, sobald mindestens eine Person gewählt
 * ist. Eine Rolle allein filtert nichts — sie sagt nur, in welcher Rolle die
 * gewählte Person beteiligt sein muss.
 */
export function isPeopleActive(ids: readonly string[] | undefined): boolean {
  return teilePersonenAuswahl(ids).ids.length > 0;
}

/** Toggelt eine Person in der Auswahl (unveränderlich, Reihenfolge stabil). */
export function togglePerson(ids: readonly string[] | undefined, id: string): string[] {
  const cur = ids ?? [];
  return cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
}

/**
 * Filtert die Personen-Optionen nach einem Suchbegriff (case-insensitiv) — im
 * Namen UND im Kürzel (T-245: „MaF" fand die Kollegin nicht, obwohl das Kürzel
 * im Avatar daneben steht; in der App wird überall mit Kürzeln gearbeitet).
 */
export function filterPeopleOptions<T extends { label: string; initials?: string }>(
  options: readonly T[],
  needle: string,
): T[] {
  const n = needle.trim().toLowerCase();
  if (!n) return options.slice();
  return options.filter(
    (o) => o.label.toLowerCase().includes(n) || (o.initials ?? "").toLowerCase().includes(n),
  );
}

/**
 * Liegt der Zeitpunkt (ms) im Datumsbereich? Verglichen wird der KALENDERTAG in
 * Europe/Berlin (nicht UTC — sonst rutscht eine Mail von 0:30 Uhr in den
 * Vortag). Ohne aktive Grenze passt alles; ein unbekannter Zeitpunkt (0) passt
 * nur, solange nicht gefiltert wird.
 */
export function zeitImDatumsbereich(zeit: number, v: DateRangeValue | undefined): boolean {
  const n = normalizeDateRange(v);
  if (!n.von && !n.bis) return true;
  if (!zeit || !Number.isFinite(zeit)) return false;
  // „sv-SE" formatiert als YYYY-MM-DD — direkt mit den ISO-Grenzen vergleichbar.
  const tag = new Date(zeit).toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });
  if (n.von && tag < n.von) return false;
  if (n.bis && tag > n.bis) return false;
  return true;
}
